import { mapRemoteESGIndicatorToModel } from "src/app/core/valuation-process/_models/esg-indicator.model"
import { VCHouseRemote } from "../api/comparable-house"
import { VCLandRemote } from "../api/comparable-land"
import { VCOfficeRemote } from "../api/comparable-office"
import { VCParkingRemote } from "../api/comparable-parking"
import { VCRetailBuildingRemote } from "../api/comparable-retail-building"
import { VCRetailShopRemote } from "../api/comparable-retail-shop"
import { VCWarehouseRemote } from "../api/comparable-warehouse"
import { VLandAreaComponentRemote } from "../api/land-area"
import { convertSize, VAssetClassSize, VLandAboutProperty, VLandAboutPropertyBase } from "../common"
import { displayConsiderations, VAssetClassConsiderationLand } from "../common/consideration"
import { VCHouseModel } from "../house"
import { VCLandModel } from "../land"
import { VCOfficeModel } from "../office"
import { VCParkingModel } from "../parking"
import { VCRetailBuildingModel } from "../retail-building"
import { VCRetailShopModel } from "../retail-shop"
import { VCWarehouseModel } from "../warehouse"
import { VCSpecialPurposeModel } from '../special-purpose'
import { VCSpecialPurposeRemote } from '../api/comparable-special-purpose'
import { mapVLandAboutProperty } from "./about-property"
import { mapConsiderationRemoteToDomain, mapVLandConsiderationRemoteToDomain } from "./consideration"
import { mapVHouseExternalAspectRemoteToDomain, mapVRetailBuildingExternalAspectRemoteToDomain, mapVWarehouseExternalAspectRemoteToDomain } from "./external-aspect"
import { mapVHouseGroundRemoteToDomain, mapVOfficeGroundRemoteToDomain, mapVRetailBuildingGroundRemoteToDomain, mapVRetailShopGroundRemoteToDomain, mapVWarehouseGroundRemoteToDomain } from "./ground"
import { mapVHouseInternalAspectRemoteToDomain, mapVOfficeInternalAspectRemoteToDomain, mapVRetailBuildingInternalAspectRemoteToDomain, mapVRetailShopInternalAspectRemoteToDomain, mapVWarehouseInternalAspectRemoteToDomain } from "./internal-aspect"
import { mapVParcelIdentificationRemoteToDomain } from "./land-parcel"
import { mapVLandLocationRemoteToDomain, mapVLocationRemoteToDomain } from "./location-data"
import { mapVServiceAndInfraRemoteToDomain } from "./services-and-infra"
import { mapVAssetClassSizeRemoteToDomain, mapVLandSize } from "./size"

export function mapVCHouseRemoteToDomain(remote: VCHouseRemote): VCHouseModel {
  const considerations = mapConsiderationRemoteToDomain(remote.considerations)
  return {
    id: remote.id,
    refNum: remote.ref_num,
    propertySubType: 'House',
    locationData: mapVLocationRemoteToDomain(remote.location_data),
    property: {
      subTypeCategory: remote.sub_type_category_name,
      subCategory: remote.sub_category_name,
      stateOfRepair: remote.state_of_repair_name,
      handoverStandard: remote.handover_standard_name,
      aboveFloors: Number(remote.above_floors),
      belowFloors: Number(remote.below_floors),
      energyEfficiencyGrade: remote.energy_efficiency_grade_name,
      generalDescription: remote.general_description,
      constructionDescription: remote.construction_desc,
      propertyGrade: remote.property_grade_name,
      completionYear: remote.completion_year,
      approximateYear: remote.apprx_year_extended,
      foundationType: remote.foundation_type_name
    },
    externalAspect: mapVHouseExternalAspectRemoteToDomain(remote.external_aspect),
    internalAspect: mapVHouseInternalAspectRemoteToDomain(remote.internal_aspect),
    serviceAndInfra: mapVServiceAndInfraRemoteToDomain(remote.service_and_infra),
    ground: mapVHouseGroundRemoteToDomain(remote.ground),
    sizes: mapVAssetClassSizeRemoteToDomain(remote.sizes),
    considerations,
    displayConsiderations: displayConsiderations(considerations),
    status: remote.status,
    usedTimes: remote.used_times,
    propertySubTypeName: remote.property_sub_type_name,
    propertyTypeName: remote.property_type_name,
    pictures: remote.pictures,
    parcelIdentification: mapVParcelIdentificationRemoteToDomain(remote.parcel_identification),
    esgIndicator: mapRemoteESGIndicatorToModel(remote.esgIndicator)
  }
}

export function mapComparableOfficeRemoteToDomain(comparable: VCOfficeRemote): VCOfficeModel {
  const considerations = mapConsiderationRemoteToDomain(comparable.considerations)
  return {
    id: comparable.id,
    refNum: comparable.refNum,
    propertySubType: 'Office',
    propertySubTypeName: comparable.propertySubTypeName,
    propertyTypeName: comparable.topPropertyTypeName,
    status: comparable.status,
    usedTimes: comparable.usedTimes,
    locationData: {
      latitude: Number(comparable.locationData.latitude),
      longitude: Number(comparable.locationData.longitude),
      country: comparable.locationData.countryName,
      city: comparable.locationData.cityName,
      zipCode: comparable.locationData.zipCode,
      address: comparable.locationData.address,
      locationGrade: comparable.locationData.locationGradeName,
      locationSurrounding: null,
      timezoneOffset: comparable.locationData.timezoneOffset
    },
    sizes: mapVAssetClassSizeRemoteToDomain(comparable.sizes),
    internalAspect: mapVOfficeInternalAspectRemoteToDomain(comparable.internal_aspect),
    property: {
      subCategory: comparable.subCategoryName,
      subTypeCategory: comparable.subTypeCategoryName,
      stateOfRepair: comparable.state_of_repair_name,
      handoverStandard: comparable.handover_standard_name,
      generalDescription: comparable.general_description,
      floors: comparable.floor_location,
      energyEfficiencyGrade: comparable.energy_efficiency_grade_name,
    },
    oldBuildingInfo: comparable.oldBuildingInfo,
    building: comparable.buildingInfo,
    considerations,
    displayConsiderations: displayConsiderations(considerations),
    pictures: comparable.pictures,
    ground: mapVOfficeGroundRemoteToDomain(comparable.ground),
    esgIndicator: mapRemoteESGIndicatorToModel(comparable.esgIndicator)
  }
}

export function mapVCParkingRemoteToDomain(remote: VCParkingRemote): VCParkingModel {
  const considerations = mapConsiderationRemoteToDomain(remote.considerations)
  return {
    id: remote.id,
    refNum: remote.ref_num,
    propertySubType: 'Parking',
    locationData: mapVLocationRemoteToDomain(remote.location_data),
    property: {
      subTypeCategory: remote.sub_type_category_name,
      subCategory: remote.sub_category_name,
      stateOfRepair: remote.state_of_repair_name,
      handoverStandard: remote.handover_standard_name,
      generalDescription: remote.general_description,
      floors: remote.floor_location,
      numberOfUnits: remote.number_of_units,
      parkingType: remote.parking_type_name,
      automation: remote.automation
    },
    sizes: mapVAssetClassSizeRemoteToDomain(remote.sizes),
    considerations,
    displayConsiderations: displayConsiderations(considerations),
    status: remote.status,
    usedTimes: remote.used_times,
    propertySubTypeName: remote.property_sub_type_name,
    propertyTypeName: remote.property_type_name,
    pictures: remote.pictures,
    parcelIdentification: mapVParcelIdentificationRemoteToDomain(remote.parcel_identification),
    building: remote.buildingInfo,
    oldBuildingInfo: remote.oldBuildingInfo,
    esgIndicator: mapRemoteESGIndicatorToModel(remote.esgIndicator)
  }
}

export function mapVCRetailBuildingRemoteToDomain(remote: VCRetailBuildingRemote): VCRetailBuildingModel {
  const considerations = mapConsiderationRemoteToDomain(remote.considerations)
  return {
    id: remote.id,
    refNum: remote.ref_num,
    propertySubType: 'Retail Building',
    locationData: mapVLocationRemoteToDomain(remote.location_data),
    property: {
      subTypeCategory: remote.sub_type_category_name,
      subCategory: remote.sub_category_name,
      stateOfRepair: remote.state_of_repair_name,
      handoverStandard: remote.handover_standard_name,
      aboveFloors: Number(remote.above_floors),
      belowFloors: Number(remote.below_floors),
      energyEfficiencyGrade: remote.energy_efficiency_grade_name,
      generalDescription: remote.general_description,
      constructionDescription: remote.construction_desc,
      propertyGrade: remote.property_grade_name,
      completionYear: remote.completion_year,
      approximateYear: remote.apprx_year_extended,
      foundationType: remote.foundation_type_name
    },
    externalAspect: mapVRetailBuildingExternalAspectRemoteToDomain(remote.external_aspect),
    internalAspect: mapVRetailBuildingInternalAspectRemoteToDomain(remote.internal_aspect),
    ground: mapVRetailBuildingGroundRemoteToDomain(remote.ground),
    sizes: mapVAssetClassSizeRemoteToDomain(remote.sizes),
    considerations,
    displayConsiderations: displayConsiderations(considerations),
    status: remote.status,
    usedTimes: remote.used_times,
    propertySubTypeName: remote.property_sub_type_name,
    propertyTypeName: remote.property_type_name,
    pictures: remote.pictures,
    parcelIdentification: mapVParcelIdentificationRemoteToDomain(remote.parcel_identification),
    esgIndicator: mapRemoteESGIndicatorToModel(remote.esgIndicator)
  }
}

export function mapVCRetailShopRemoteToDomain(remote: VCRetailShopRemote): VCRetailShopModel {
  const considerations = mapConsiderationRemoteToDomain(remote.considerations)
  return {
    id: remote.id,
    refNum: remote.ref_num,
    propertySubType: 'Retail Shop',
    propertySubTypeName: remote.property_sub_type_name,
    propertyTypeName: remote.property_type_name,
    status: remote.status,
    usedTimes: remote.used_times,
    locationData: mapVLocationRemoteToDomain(remote.location_data),
    sizes: mapVAssetClassSizeRemoteToDomain(remote.sizes),
    internalAspect: mapVRetailShopInternalAspectRemoteToDomain(remote.internal_aspect),
    ground: mapVRetailShopGroundRemoteToDomain(remote.ground),
    building: remote.buildingInfo,
    oldBuildingInfo: remote.oldBuildingInfo,
    property: {
      subCategory: remote.sub_category_name,
      subTypeCategory: remote.sub_type_category_name,
      stateOfRepair: remote.state_of_repair_name,
      handoverStandard: remote.handover_standard_name,
      generalDescription: remote.general_description,
      floors: remote.floor_location,
    },
    considerations,
    displayConsiderations: displayConsiderations(considerations),
    pictures: remote.pictures,
    esgIndicator: mapRemoteESGIndicatorToModel(remote.esgIndicator)
  }
}

export function mapVCWarehouseRemoteToDomain(remote: VCWarehouseRemote): VCWarehouseModel {
  const considerations = mapConsiderationRemoteToDomain(remote.considerations)
  return {
    id: remote.id,
    refNum: remote.ref_num,
    propertySubType: 'Warehouse',
    locationData: mapVLocationRemoteToDomain(remote.location_data),
    property: {
      subTypeCategory: remote.sub_type_category_name,
      subCategory: remote.sub_category_name,
      stateOfRepair: remote.state_of_repair_name,
      handoverStandard: remote.handover_standard_name,
      aboveFloors: Number(remote.above_floors),
      belowFloors: Number(remote.below_floors),
      energyEfficiencyGrade: remote.energy_efficiency_grade_name,
      generalDescription: remote.general_description,
      constructionDescription: remote.construction_desc,
      propertyGrade: remote.property_grade_name,
      completionYear: remote.completion_year,
      foundationType: remote.foundation_type_name
    },
    externalAspect: mapVWarehouseExternalAspectRemoteToDomain(remote.external_aspect),
    internalAspect: mapVWarehouseInternalAspectRemoteToDomain(remote.internal_aspect),
    ground: mapVWarehouseGroundRemoteToDomain(remote.ground),
    sizes: mapVAssetClassSizeRemoteToDomain(remote.sizes),
    considerations,
    displayConsiderations: displayConsiderations(considerations),
    status: remote.status,
    usedTimes: remote.used_times,
    propertySubTypeName: remote.property_sub_type_name,
    propertyTypeName: remote.property_type_name,
    pictures: remote.pictures,
    parcelIdentification: mapVParcelIdentificationRemoteToDomain(remote.parcel_identification),
    esgIndicator: mapRemoteESGIndicatorToModel(remote.esgIndicator)
  }
}

export function mapVCLandRemoteToDomain(remote: VCLandRemote): VCLandModel {
  const considerations = mapConsiderationRemoteToDomain(remote.considerations)
  const locationData = mapVLandLocationRemoteToDomain(remote.location_data)
  const propertyBase = {
    landParcelName: remote.land_parcel_name,
    coordinateReferencySystem: remote.coordinate_reference_system_name,
    qualityOfSoilDegradation: remote.qos_degradation_name,
    comparativeLandUse: remote.comparative_land_use_name,
    landCoverType: remote.land_cover_type,
    soureOfLandUseClassification: remote.sol_use_classification_name,
    otherCharacteristics: remote.other_characteristics,
    landUseApproval: remote.land_use_approval_implementation
  }
  return {
    id: remote.id,
    propertySubType: 'Land',

    refNum: remote.ref_num,
    locationData: locationData,

    status: remote.status,
    usedTimes: remote.usedTimes,
    propertySubTypeName: remote.property_sub_type_name,

    sizes: mapVLandSize(remote.land_area_components),
    pictures: remote.pictures,

    considerations,
    displayConsiderations: displayConsiderations(considerations),

    propertyTypeName: 'Land',

    property: mapVLandAboutProperty(propertyBase, remote.building_exist, remote.buildings)
  }
}

export function mapVCSpecialPurposeRemoteToDomain(remote: VCSpecialPurposeRemote): VCSpecialPurposeModel {
  const considerations = mapConsiderationRemoteToDomain(remote.considerations)

  return {
    id: remote.id,
    propertySubType: 'Special Purpose',
    propertySubTypeName: remote.property_sub_type_name ? remote.property_sub_type_name : 'Special Purpose',
    locationData: mapVLocationRemoteToDomain(remote.location_data),

    refNum: remote.ref_num,

    sizes: mapVAssetClassSizeRemoteToDomain(remote.sizes),
    pictures: remote.pictures,
    considerations,
    displayConsiderations: displayConsiderations(considerations),
    externalAspect: mapVWarehouseExternalAspectRemoteToDomain(remote.external_aspect),
    internalAspect: mapVWarehouseInternalAspectRemoteToDomain(remote.internal_aspect),
    ground: mapVWarehouseGroundRemoteToDomain(remote.ground),

    status: remote.status,
    usedTimes: remote.used_times,
    parcelIdentification: mapVParcelIdentificationRemoteToDomain(remote.parcel_identification),
    esgIndicator: mapRemoteESGIndicatorToModel(remote.esgIndicator),
    property: {
      specialPurposeType: remote.special_purpose_type
    },
    propertyTypeName: remote.property_type_name
  }
}

function landDisplayConsiderations(considerations: VAssetClassConsiderationLand[]): VAssetClassConsiderationLand[] {
  const items = []
  if (considerations.length > 0) {
    items.push(considerations[0])
  }
  return items
}